import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { addDoc, collection, serverTimestamp, query, where, getDocs, setDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Channel } from '../../types/messages';
import toast from 'react-hot-toast';

interface CreateChannelModalProps {
  onClose: () => void;
  schoolId: string;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

export default function CreateChannelModal({ onClose, schoolId }: CreateChannelModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<Channel['type']>('department');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  useEffect(() => {
    const loadSchoolMembers = async () => {
      try {
        // Get all school members
        const membersQuery = query(
          collection(db, 'school_members'),
          where('schoolId', '==', schoolId)
        );
        const memberSnapshot = await getDocs(membersQuery);
        
        // Get user details for each member
        const userPromises = memberSnapshot.docs.map(async (memberDoc) => {
          const userDoc = await getDocs(
            query(
              collection(db, 'users'),
              where('__name__', '==', memberDoc.data().userId)
            )
          );
          
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            return {
              id: memberDoc.data().userId,
              name: userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
              email: userData.email || ''
            };
          }
          return null;
        });

        const users = (await Promise.all(userPromises)).filter((user): user is UserOption => user !== null);
        setUsers(users);
      } catch (error) {
        console.error('Error loading school members:', error);
      }
    };

    loadSchoolMembers();
  }, [schoolId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter a channel name');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Include the creator in the members list
      const allMembers = [...new Set([user.uid, ...selectedMembers])];

      // Create the channel document
      const docRef = await addDoc(collection(db, 'channels'), {
        name: name.trim(),
        description: description.trim(),
        type,
        schoolId,
        createdBy: user.uid,
        members: allMembers,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Create channel_members subcollection entries
      const memberPromises = allMembers.map(async (memberId) => {
        const role = memberId === user.uid ? 'owner' : 'member';
        return setDoc(doc(db, 'channels', docRef.id, 'members', memberId), {
          userId: memberId,
          role,
          joinedAt: serverTimestamp(),
          notifications: {
            muted: false,
            highlights: []
          }
        });
      });

      await Promise.all(memberPromises);

      toast.success('Channel created successfully');
      onClose();
    } catch (error) {
      console.error('Error creating channel:', error);
      toast.error('Failed to create channel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-xl font-semibold text-text-primary">Create Channel</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-secondary mb-1">
              Channel Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Science Department"
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-1">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this channel about?"
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              rows={3}
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-text-secondary mb-1">
              Channel Type
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as Channel['type'])}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
            >
              <option value="department">Department</option>
              <option value="committee">Committee</option>
              <option value="grade">Grade Level</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label htmlFor="members" className="block text-sm font-medium text-text-secondary mb-1">
              Members
            </label>
            <select
              id="members"
              multiple
              value={selectedMembers}
              onChange={(e) => setSelectedMembers(Array.from(e.target.selectedOptions, option => option.value))}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              size={5}
            >
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-text-secondary mt-1">
              Hold Ctrl (or Cmd) to select multiple members. You will be added automatically.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}